import { IconButton } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import HistoryIcon from "@material-ui/icons/History";
import { ReactNode } from "react";
import styled from "styled-components";

// Two columns: the value takes the free width and the buttons stay on its right.
const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 0.5rem;
  align-items: center;
`;

const StyledValue = styled.span<{ noWrap: boolean; minWidth?: string }>`
  ${({ noWrap }) =>
    noWrap
      ? "white-space: nowrap;"
      : "white-space: pre-line; overflow-wrap: break-word;"}
  ${({ minWidth }) => (minWidth ? `min-width: ${minWidth};` : "")}
`;

// Always as wide as both buttons, so the icons line up from row to row even
// when one of them is not shown.
const StyledActions = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 3rem;
`;

const StyledIconButton = styled(IconButton)`
  padding: 0.25rem;
  .MuiSvgIcon-root {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledIconPlaceholder = styled.span`
  width: 1.5rem;
`;

type TContractFieldCellProps = {
  value?: ReactNode;
  // Short tabulated values (a valuation) must stay on one line.
  noWrap?: boolean;
  // Long texts (a note) wrap, but need room to be read as a paragraph.
  minValueWidth?: string;
  editLabel: string;
  historyLabel: string;
  showEdit: boolean;
  showHistory: boolean;
  onEdit: () => void;
  onHistory: () => void;
};

/**
 * A contract field shown in a table cell with, on its right, the button to
 * edit it and then the one to see the history of its editions.
 */
const ContractFieldCell = ({
  value,
  noWrap = false,
  minValueWidth,
  editLabel,
  historyLabel,
  showEdit,
  showHistory,
  onEdit,
  onHistory,
}: TContractFieldCellProps) => {
  const valueElement = (
    <StyledValue noWrap={noWrap} minWidth={minValueWidth}>
      {value}
    </StyledValue>
  );

  if (!showEdit && !showHistory) {
    return valueElement;
  }

  return (
    <StyledWrapper>
      {valueElement}
      <StyledActions>
        {showEdit ? (
          <StyledIconButton
            color="primary"
            size="small"
            aria-label={editLabel}
            title={editLabel}
            onClick={onEdit}
          >
            <EditIcon />
          </StyledIconButton>
        ) : (
          <StyledIconPlaceholder />
        )}
        {showHistory ? (
          <StyledIconButton
            color="primary"
            size="small"
            aria-label={historyLabel}
            title={historyLabel}
            onClick={onHistory}
          >
            <HistoryIcon />
          </StyledIconButton>
        ) : (
          <StyledIconPlaceholder />
        )}
      </StyledActions>
    </StyledWrapper>
  );
};

export default ContractFieldCell;
